/**
 * mediaCapture — unified capture helper for photo + video.
 *
 * rewrite: STOP storing dataUrls. The app is a launcher for the
 * device camera, not a media host. Captured media is persisted to the
 * user's device (native = Capacitor Filesystem; web = in-memory Blob URL)
 * and only METADATA is kept in the session record. Free tier ships zero
 * binary to cloud. Pro tier (later) will layer an opt-in upload step.
 *
 * Payload shape returned by capture flows:
 *   {
 *     kind: 'media',
 *     name: 'capture-20260606-193412.mp4',
 *     type: 'video/mp4',
 *     size: 47_182_400,                  // bytes
 *     capturedAt: '2026-06-06T19:34:12.000Z',
 *     localUri: 'capacitor://localhost/_capacitor_file_/...'  // native
 *             | 'blob:https://...'                            // web (ephemeral)
 *             | null,                                         // unavailable
 *     persistedPath: 'enferno/captures/<name>',                // native only
 *   }
 *
 * `localUri` is the playback source for inline <video> / <img>. It is
 * NEVER cloud-synced — see ProfileContext.addTrainingSession which strips
 * it from the outgoing POST body.
 */
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';

const PERSIST_DIR = 'enferno/captures';

// Build a default filename: capture-YYYYMMDD-HHMMSS.<ext>
const defaultName = (mimeType) => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  const ext = (mimeType || 'application/octet-stream').split('/')[1] || 'bin';
  return `capture-${stamp}.${ext}`;
};

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => {
    const result = r.result || '';
    const comma = result.indexOf(',');
    resolve(comma >= 0 ? result.slice(comma + 1) : result);
  };
  r.onerror = reject;
  r.readAsDataURL(blob);
});

const base64ToBlob = (base64, mimeType = 'image/jpeg') => {
  const byteChars = atob(base64);
  const byteNums = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i += 1) {
    byteNums[i] = byteChars.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNums)], { type: mimeType });
};

/**
 * persistMediaToDevice — write a Blob to durable device storage and
 * return a localUri suitable for inline playback. Native uses the
 * Capacitor Filesystem (writes into the app's Data directory and returns
 * a webview-readable URI via Capacitor.convertFileSrc). Web creates an
 * in-memory Blob URL which lives until the page reloads (browser
 * security prevents persistent local file access).
 *
 * Returns { localUri, persistedPath } or { localUri: null } on failure.
 */
export const persistMediaToDevice = async (blob, fileName) => {
  const name = fileName || defaultName(blob.type);

  if (Capacitor.isNativePlatform()) {
    try {
      const base64 = await blobToBase64(blob);
      const path = `${PERSIST_DIR}/${name}`;
      const res = await Filesystem.writeFile({
        path,
        data: base64,
        directory: Directory.Data,
        recursive: true,
      });
      // res.uri is a `file://` path on iOS/Android — convert it to a
      // webview-loadable URL so <video src=...> works inline.
      const localUri = Capacitor.convertFileSrc(res.uri);
      return { localUri, persistedPath: path };
    } catch (err) {
      console.warn('Filesystem write failed; falling back to Blob URL:', err?.message);
      // Fall through to Blob URL fallback below.
    }
  }

  // Web (or native fallback): ephemeral Blob URL. Good for the current
  // page lifetime; lost on reload. Honest UX — render the playback now,
  // show filename-only after reload.
  try {
    const localUri = URL.createObjectURL(blob);
    return { localUri, persistedPath: null };
  } catch {
    return { localUri: null, persistedPath: null };
  }
};

const blobToPayload = async (blob, fileName) => {
  const name = fileName || defaultName(blob.type);
  const { localUri, persistedPath } = await persistMediaToDevice(blob, name);
  return {
    kind: 'media',
    name,
    type: blob.type || 'application/octet-stream',
    size: blob.size,
    capturedAt: new Date().toISOString(),
    localUri,
    persistedPath,
  };
};

/**
 * capturePhoto — opens the native camera (or returns null on web so the
 * caller can use the <input capture> fallback). Native flow writes the
 * photo to durable storage and returns a localUri.
 */
export const capturePhoto = async () => {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const photo = await Camera.getPhoto({
      quality: 80,
      allowEditing: false,
      resultType: CameraResultType.Base64,
      source: CameraSource.Camera,
      saveToGallery: true,  // Also save to user's Camera Roll — their file, their copy
    });
    const mimeType = photo.format ? `image/${photo.format}` : 'image/jpeg';
    const blob = base64ToBlob(photo.base64String, mimeType);
    return blobToPayload(blob, defaultName(mimeType));
  } catch (err) {
    if (err?.message?.includes('cancel') || err?.message?.includes('denied')) return null;
    throw err;
  }
};

/**
 * pickFromLibrary — opens the device photo library picker via Capacitor.
 * Web returns null (caller uses standard file input).
 */
export const pickFromLibrary = async () => {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const photo = await Camera.getPhoto({
      quality: 80,
      allowEditing: false,
      resultType: CameraResultType.Base64,
      source: CameraSource.Photos,
    });
    const mimeType = photo.format ? `image/${photo.format}` : 'image/jpeg';
    const blob = base64ToBlob(photo.base64String, mimeType);
    return blobToPayload(blob, defaultName(mimeType));
  } catch (err) {
    if (err?.message?.includes('cancel') || err?.message?.includes('denied')) return null;
    throw err;
  }
};

/**
 * fileToPayload — convert a browser File (from <input type="file">) into
 * the same metadata payload shape. Persists the File contents to device.
 */
export const fileToPayload = async (file) => {
  return blobToPayload(file, file.name);
};

/**
 * urlToPayload — produce an attachment payload from a pasted external
 * URL (YouTube, Vimeo, Drive, etc.). No device storage — just the URL.
 */
export const urlToPayload = (url) => ({
  kind: 'url',
  url,
  capturedAt: new Date().toISOString(),
});

/**
 * Detect a YouTube video ID from common URL forms. Returns id string or null.
 */
export const youtubeIdFromUrl = (url) => {
  if (!url) return null;
  // youtu.be/<id>
  let m = url.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/);
  if (m) return m[1];
  // youtube.com/watch?v=<id>
  m = url.match(/[?&]v=([A-Za-z0-9_-]{6,})/);
  if (m) return m[1];
  // youtube.com/shorts/<id>
  m = url.match(/youtube\.com\/shorts\/([A-Za-z0-9_-]{6,})/);
  if (m) return m[1];
  // youtube.com/embed/<id>
  m = url.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/);
  if (m) return m[1];
  return null;
};

/**
 * Detect a Vimeo video ID. Returns id string or null.
 */
export const vimeoIdFromUrl = (url) => {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
  return m ? m[1] : null;
};

export const isNativePlatform = () => Capacitor.isNativePlatform();
